import React, { useState, useEffect, useRef, useCallback } from "react";

import { useSelector } from "react-redux";

import { useBlogContext } from "../../context/blog_context";

import { formatDateWithSuffixV2, getOrCreateGuestId } from "../../utils";

import {
    GoogleAnalyticsEnum,
    triggerFunction,
} from "../../utils/google-analytics";

import ModalLogin from "../form/ModalLogin";
import Spinner from "../../components/spinner-component/SpinnerOne";
import { useQuickInsightContext } from "../../context/quick_insights_context";
import InteractionComponent from "../reusable/InteractionComponent";
import BookmarkComponent from "../reusable/BookmarkComponent";

const QUOTES_STORAGE_KEY = "longswitch_academy_saved_quotes";

function KnowledgeArticle({
    selectedArticle,
    setArticle,
    prevData,
    nextData,
    onReadMore,
    onReadMoreClose,
}) {
    const { loginData } = useSelector((item) => item.user);

    const { isLoading, quickInsights, insightsUserInteraction } =
        useQuickInsightContext();

    const [isOpen, setIsOpen] = useState(false);

    const [isLiked, setIsLiked] = useState(false);

    const [isCopied, setIsCopied] = useState(false);
    const [likesCount, setLikesCount] = useState(0);

    const [isLoggedOut, setIsLoggedOut] = useState(false);

    const [completionPercentage, setCompletionPercentage] = useState(0);

    const [savedQuotes, setSavedQuotes] = useState([]);

    const [showQuotes, setShowQuotes] = useState(false);

    const [showShareMenu, setShowShareMenu] = useState(false);

    const [selection, setSelection] = useState(null);

    const [annotationToolbar, setAnnotationToolbar] = useState({
        visible: false,
        x: 0,
        y: 0,
    });

    const [toast, setToast] = useState("");
    const [text, setText] = useState("");

    const readerBodyRef = useRef(null);

    const articleContentRef = useRef(null);

    const selectionToolbarRef = useRef(null);

    const member = (loginData && loginData.data) || null;

    // const currentUserId = member
    //     ? member?.userRecordId || ""
    //     : getOrCreateGuestId();
    const currentUserId = "6a92bcbac8f04e2a6167a865";

    // liked
    const matchedLikedData = insightsUserInteraction?.filter(
        (i) => i?.userId === currentUserId && i?.insightId === selectedArticle?.id,
    );
    const liked =
        matchedLikedData && matchedLikedData.length
            ? matchedLikedData[0]?.isLiked
            : false;

    useEffect(() => {
        if (!selectedArticle) {
            return;
        }

        setIsOpen(true);

        setIsCopied(false);

        setShowQuotes(false);

        setShowShareMenu(false);

        setSelection(null);

        setAnnotationToolbar({
            visible: false,
            x: 0,
            y: 0,
        });

        setCompletionPercentage(Number(selectedArticle?.completionPercentage || 0));

        /*
         * Load saved quotes
         */
        try {
            const storedQuotes =
                JSON.parse(localStorage.getItem(QUOTES_STORAGE_KEY)) || [];

            setSavedQuotes(storedQuotes);
        } catch (error) {
            console.error("Unable to load saved quotes", error);

            setSavedQuotes([]);
        }

        /*
         * Reset reader scroll
         */
        requestAnimationFrame(() => {
            if (readerBodyRef.current) {
                readerBodyRef.current.scrollTop = 0;
            }
        });

        // Set Auto Likes
        let likes = [...(selectedArticle?.likes || [])];

        const index = (likes || []).findIndex(
            (item) => item?.userId === currentUserId && item?.isLiked === true,
        );

        if (index !== -1) {
            setIsLiked(true);
        } else {
            setIsLiked(false);
        }

        // likes count
        const filteredLikes = (likes || []).filter(
            (item) => item?.isLiked === true,
        );
        setLikesCount(filteredLikes?.length);
    }, [selectedArticle]);

    useEffect(() => {
        if (!selectedArticle) {
            return;
        }

        let updatedBlogs = (quickInsights || []).filter(
            (item) => item?.id === selectedArticle?.id,
        );

        if (updatedBlogs && updatedBlogs.length) {
            // Set Auto Likes
            let likes = [...(updatedBlogs[0]?.likes || [])];

            const index = (likes || []).findIndex(
                (item) => item?.userId === currentUserId && item?.isLiked === true,
            );

            if (index !== -1) {
                setIsLiked(true);
            } else {
                setIsLiked(false);
            }

            // likes count
            const filteredLikes = (likes || []).filter(
                (item) => item?.isLiked === true,
            );
            setLikesCount(filteredLikes?.length);
        }
    }, [quickInsights]);

    useEffect(() => {
        if (!isOpen) {
            return;
        }

        const originalOverflow = document.body.style.overflow;

        document.body.style.overflow = "hidden";

        return () => {
            document.body.style.overflow = originalOverflow;
        };
    }, [isOpen]);

    useEffect(() => {
        if (!isOpen) {
            return;
        }

        const container = readerBodyRef.current;

        if (!container) {
            return;
        }

        const calculateProgress = () => {
            const scrollTop = container.scrollTop;

            const scrollHeight = container.scrollHeight;

            const clientHeight = container.clientHeight;

            const totalScrollableHeight = scrollHeight - clientHeight;

            if (totalScrollableHeight <= 0) {
                setCompletionPercentage(100);

                return;
            }

            const percentage = (scrollTop / totalScrollableHeight) * 100;

            setCompletionPercentage(
                Math.min(100, Math.max(0, Math.round(percentage))),
            );
        };

        container.addEventListener("scroll", calculateProgress, { passive: true });

        calculateProgress();

        return () => {
            container.removeEventListener("scroll", calculateProgress);
        };
    }, [isOpen, selectedArticle]);

    useEffect(() => {
        if (!isOpen) {
            return;
        }

        const handleKeyDown = (event) => {
            if (event.key === "Escape") {
                if (showQuotes) {
                    setShowQuotes(false);

                    return;
                }

                if (annotationToolbar.visible) {
                    clearSelection();

                    return;
                }

                handleClose();
            }
        };

        document.addEventListener("keydown", handleKeyDown);

        return () => {
            document.removeEventListener("keydown", handleKeyDown);
        };
    }, [isOpen, showQuotes, annotationToolbar.visible]);

    const showToast = useCallback((message) => {
        setToast(message);

        setTimeout(() => {
            setToast("");
        }, 2200);
    }, []);

    const handleClose = () => {
        setIsOpen(false);

        setShowQuotes(false);

        setShowShareMenu(false);

        clearSelection();

        setArticle(null);

        if (onReadMoreClose) {
            onReadMoreClose();
        }
    };

    // const handleLike = (value) => {

    //     try {

    //         if (!member) {

    //             openModalLogin();

    //             return;
    //         }

    //         let likes = [
    //             ...(selectedArticle?.likes || [])
    //         ];

    //         const index =
    //             likes.findIndex(
    //                 (item) =>
    //                     item.userId ===
    //                     currentUserId
    //             );

    //         if (index !== -1) {

    //             likes = likes.map(
    //                 (like, i) =>
    //                     i === index
    //                         ? {
    //                             ...like,
    //                             count: value ? 1 : 0,
    //                             isLiked: value,
    //                         }
    //                         : like
    //             );

    //         } else {

    //             likes.push({

    //                 userId: currentUserId,

    //                 count: value ? 1 : 0,

    //                 isLiked: value,

    //                 idValue:
    //                     selectedArticle?.id || "",

    //                 id:
    //                     selectedArticle?.id || "",
    //             });

    //         }

    //         updateBlogLikes(
    //             selectedArticle?.id || "",
    //             likes
    //         );

    //         setIsLiked(value);

    //         triggerFunction(
    //             window.location.pathname +
    //             " " +
    //             GoogleAnalyticsEnum.BUTTON,

    //             GoogleAnalyticsEnum
    //                 .LSA_SALESFORCE_BLOG_LIKE_ADDED
    //         );

    //     } catch (error) {

    //         console.error(
    //             "Error while updating likes => ",
    //             error
    //         );

    //     }

    // };

    const handleShare = async () => {
        const shareUrl = window.location.href;

        const shareData = {
            title: selectedArticle?.title || "Longswitch Academy",

            text: `Check out this Salesforce insight from Longswitch Academy: ${selectedArticle?.title || ""
                }`,

            url: shareUrl,
        };

        if (navigator.share) {
            try {
                await navigator.share(shareData);

                return;
            } catch (error) {
                if (error?.name === "AbortError") {
                    return;
                }
            }
        }

        try {
            await navigator.clipboard.writeText(shareUrl);

            setIsCopied(true);

            showToast("Article link copied");

            setTimeout(() => {
                setIsCopied(false);
            }, 2000);
        } catch (error) {
            console.error("Unable to copy article link:", error);
        }
    };

    const handleTextSelection = () => {
        const selectionObject = window.getSelection();

        if (!selectionObject) {
            return;
        }

        const text = selectionObject.toString().trim();

        if (!text) {
            clearSelection();

            return;
        }

        if (!articleContentRef.current) {
            return;
        }

        const range = selectionObject.getRangeAt(0);

        /*
         * Make sure selection belongs
         * to the article.
         */
        if (!articleContentRef.current.contains(range.commonAncestorContainer)) {
            return;
        }

        const rect = range.getBoundingClientRect();

        const containerRect = readerBodyRef.current?.getBoundingClientRect();

        if (!containerRect) {
            return;
        }

        setSelection({
            text,

            articleId: selectedArticle?.id || "",

            articleTitle: selectedArticle?.title || "",
        });

        /*
         * Position toolbar relative
         * to the reader viewport.
         */
        const toolbarWidth = 330;

        let x = rect.left - containerRect.left + rect.width / 2 - toolbarWidth / 2;

        let y = rect.top - containerRect.top - 60;

        /*
         * Prevent toolbar going outside
         * the reader.
         */
        x = Math.max(12, Math.min(x, containerRect.width - toolbarWidth - 12));

        if (y < 10) {
            y = rect.bottom - containerRect.top + 12;
        }

        setAnnotationToolbar({
            visible: true,

            x,

            y,
        });
    };

    const clearSelection = () => {
        setSelection(null);

        setAnnotationToolbar({
            visible: false,

            x: 0,

            y: 0,
        });

        try {
            const currentSelection = window.getSelection();

            if (currentSelection) {
                currentSelection.removeAllRanges();
            }
        } catch (error) {
            // Ignore browser selection cleanup errors
        }
    };

    const saveQuote = () => {
        if (!selection?.text) {
            return;
        }

        const newQuote = {
            id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,

            text: selection.text,

            articleId: selection.articleId,

            articleTitle: selection.articleTitle,

            articleUrl: window.location.href,

            createdAt: new Date().toISOString(),

            source: "Longswitch Academy",
        };

        const existingQuotes =
            JSON.parse(localStorage.getItem(QUOTES_STORAGE_KEY)) || [];

        /*
         * Prevent exact duplicate.
         */
        const alreadySaved = existingQuotes.some(
            (quote) =>
                quote.text === newQuote.text && quote.articleId === newQuote.articleId,
        );

        if (alreadySaved) {
            showToast("Quote already saved");

            clearSelection();

            return;
        }

        const updatedQuotes = [newQuote, ...existingQuotes].slice(0, 100);

        localStorage.setItem(
            QUOTES_STORAGE_KEY,

            JSON.stringify(updatedQuotes),
        );

        setSavedQuotes(updatedQuotes);

        showToast("Quote saved on this device");

        clearSelection();
    };

    const deleteQuote = (quoteId) => {
        const updatedQuotes = savedQuotes.filter((quote) => quote.id !== quoteId);

        setSavedQuotes(updatedQuotes);

        localStorage.setItem(
            QUOTES_STORAGE_KEY,

            JSON.stringify(updatedQuotes),
        );

        showToast("Quote removed");
    };

    const copyQuote = async (text) => {
        try {
            await navigator.clipboard.writeText(text);

            showToast("Quote copied");
        } catch (error) {
            console.error("Unable to copy quote", error);
        }
    };

    const searchGoogle = () => {
        if (!selection?.text) {
            return;
        }

        const googleUrl = `https://www.google.com/search?q=${encodeURIComponent(
            selection.text,
        )}`;

        window.open(googleUrl, "_blank", "noopener,noreferrer");

        clearSelection();
    };

    const shareQuote = async () => {
        if (!selection?.text) {
            return;
        }

        const shareText =
            `"${selection.text}"\n\n` +
            `— Shubham Prajapati (Founder, Longswitch Academy)\n`;

        /*
         * Native share
         */
        if (navigator.share) {
            try {
                await navigator.share({
                    title: selection.articleTitle || "Longswitch Academy",

                    text: shareText,

                    url: window.location.href,
                });

                clearSelection();

                return;
            } catch (error) {
                if (error?.name === "AbortError") {
                    return;
                }
            }
        }

        /*
         * X / Twitter fallback
         */
        const twitterUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(
            shareText,
        )}`;

        window.open(twitterUrl, "_blank", "noopener,noreferrer");

        clearSelection();
    };

    const shareSavedQuote = async (quote) => {
        // const shareText =
        //     `"${quote.text}"\n\n` +
        //     `— From Longswitch Academy\n` +
        //     `${quote.articleUrl}`;

        const shareText =
            `"${quote.text}"\n\n` +
            `— Shubham Prajapati (Founder, Longswitch Academy)\n`;

        if (navigator.share) {
            try {
                await navigator.share({
                    title: quote.articleTitle || "Longswitch Academy",

                    text: shareText,

                    url: quote.articleUrl,
                });

                return;
            } catch (error) {
                if (error?.name === "AbortError") {
                    return;
                }
            }
        }

        try {
            await navigator.clipboard.writeText(shareText);

            showToast("Quote copied for sharing");
        } catch (error) {
            console.error("Unable to share quote", error);
        }
    };

    // Handle Highlight
    const handleHighlight = () => {
        const selection = window.getSelection();

        if (!selection || selection.rangeCount === 0) {
            return;
        }

        const selectedText = selection.toString().trim();

        if (!selectedText) {
            return;
        }

        const range = selection.getRangeAt(0);

        // Make sure selection is inside the article
        const article = document.querySelector(".knowledge-article");

        if (!article || !article.contains(range.commonAncestorContainer)) {
            return;
        }

        // Create actual visual highlight
        const mark = document.createElement("mark");

        mark.className = "reader-highlight";
        mark.setAttribute("data-highlight-id", crypto.randomUUID());

        try {
            range.surroundContents(mark);
        } catch (error) {
            /*
             * surroundContents can fail when the selection crosses
             * multiple HTML elements.
             *
             * For those cases, use the fallback implementation below.
             */
            this.applyHighlightFallback(range);
        }

        selection.removeAllRanges();
    };

    const openModalLogin = () => setIsLoggedOut(true);

    const closeModalLogin = () => {
        setIsLoggedOut(false);
        setIsOpen(true);
    };

    const isReadingComplete = completionPercentage >= 95;

    if (!isOpen && isLoggedOut) {
        return (
            <ModalLogin
                show={isLoggedOut}
                onCloseModalLogin={closeModalLogin}
                title={`Login to Interact the Salesforce Quick Insight`}
            />
        );
    }

    if (!selectedArticle) {
        return null;
    }

    return (
        <div className="knowledge-v3-wrapper">
            {isLoading && <Spinner />}

            {isOpen && (
                <div
                    className="knowledge-v3-backdrop"
                    onMouseDown={(event) => {
                        if (event.target === event.currentTarget) {
                            handleClose();
                        }
                    }}
                />
            )}

            {/* =====================================================
                MAIN READER MODAL
            ===================================================== */}

            {isOpen && (
                <div
                    className="knowledge-v3-modal"
                    role="dialog"
                    aria-modal="true"
                    aria-labelledby="knowledge-v3-title"
                >
                    {/* =================================================
                        TOP HEADER
                    ================================================= */}

                    <header className="knowledge-v3-header">
                        <div className="knowledge-v3-header-left">
                            {/* <div>

                                <div className="knowledge-v3-brand">

                                    Across the Salesforce ecosystem

                                </div>

                                <div className="knowledge-v3-header-label">

                                    Salesforce Quick Insights

                                </div>

                            </div> */}

                            <div class="section-title text-start">
                                <span class="pre-title fs-9 knowledge-article-pre-title mt--0">
                                    Across the Salesforce ecosystem
                                </span>
                                <h3 class="title fs-15">Salesforce Quick Insights</h3>
                            </div>
                        </div>

                        <div className="knowledge-v3-header-actions">
                            <button
                                type="button"
                                className="knowledge-v3-close"
                                onClick={handleClose}
                                aria-label="Close article"
                            >
                                <i className="ri-close-line" />
                            </button>

                            {/* <button
                                type="button"
                                className="knowledge-v3-header-btn"
                                onClick={() =>
                                    setShowQuotes(true)
                                }
                            >

                                <i className="ri-double-quotes-l" />

                                <span>
                                    Quotes
                                </span>

                                {savedQuotes.length > 0 && (

                                    <span className="knowledge-v3-count">

                                        {savedQuotes.length}

                                    </span>

                                )}

                            </button>


                            <button
                                type="button"
                                className="knowledge-v3-header-btn"
                                onClick={handleShare}
                            >

                                <i className="ri-share-line" />

                                <span>
                                    Share
                                </span>

                            </button> */}
                        </div>
                    </header>

                    {/* =================================================
                        PROGRESS
                    ================================================= */}

                    <div className="knowledge-v3-progress">
                        <div
                            className="knowledge-v3-progress-bar"
                            style={{
                                width: `${completionPercentage}%`,
                            }}
                        />
                    </div>

                    {/* =================================================
                        SCROLL AREA
                    ================================================= */}

                    <main className="knowledge-v3-body" ref={readerBodyRef}>
                        {/* =============================================
                            ARTICLE HEADER BANNER
                        ============================================= */}

                        <section className="knowledge-v3-hero">
                            <div className="knowledge-v3-hero-pattern" />

                            <div className="knowledge-v3-hero-content">
                                <div className="knowledge-v3-category">
                                    {selectedArticle?.categories?.[0] || "SALESFORCE"}
                                </div>

                                <h1 id="knowledge-v3-title" className="knowledge-v3-title">
                                    {selectedArticle?.title || ""}
                                </h1>

                                <p className="knowledge-v3-subtitle">
                                    Practical Salesforce insights for Admins, Developers and
                                    Salesforce professionals.
                                </p>

                                <div className="knowledge-v3-meta">
                                    <span>
                                        <i className="ri-calendar-line" />

                                        {formatDateWithSuffixV2(selectedArticle?.publishedAt) || ""}
                                    </span>

                                    <span className="knowledge-v3-meta-dot">•</span>

                                    <span>
                                        <i className="ri-time-line" />

                                        {selectedArticle?.readingTime || "2 min read"}
                                    </span>

                                    <span className="knowledge-v3-meta-dot">•</span>

                                    <span>
                                        <i className="ri-book-open-line" />
                                        {completionPercentage}% read
                                    </span>
                                </div>
                            </div>
                        </section>

                        {/* =============================================
                            ARTICLE TOOLBAR
                        ============================================= */}

                        <div className="knowledge-v3-reading-toolbar">
                            <div className="knowledge-v3-reading-hint">
                                <i className="ri-mark-pen-line" />
                                Select any text to highlight, save or share it.
                            </div>

                            <div className="knowledge-v3-toolbar-actions">
                                {/** New Component */}
                                <InteractionComponent
                                    selectedArticle={selectedArticle}
                                    setIsOpen={setIsOpen}
                                    openModalLogin={openModalLogin}
                                />

                                <BookmarkComponent selectedArticle={selectedArticle} setIsOpen={setIsOpen} openModalLogin={openModalLogin} />

                                {/* <button
                                    type="button"
                                    className="knowledge-v3-mini-btn"
                                    onClick={() =>
                                        setShowQuotes(true)
                                    }
                                    aria-label="Saved quotes"
                                >

                                    <i className="ri-double-quotes-l" />

                                </button>

                                <button
                                    type="button"
                                    className="knowledge-v3-mini-btn"
                                    onClick={handleShare}
                                    aria-label="Share"
                                >

                                    <i className="ri-share-line" />

                                </button>

                                <div className="d-flex align-items-center">
                                    <button
                                        type="button"
                                        className={
                                            `knowledge-v3-mini-btn mr--5 ${isLiked
                                                ? "active"
                                                : ""
                                            }`
                                        }
                                        onClick={handleLike}
                                        aria-label="Like article"
                                    >

                                        <i
                                            className={
                                                liked
                                                    ? "ri-heart-fill text-danger"
                                                    : "ri-heart-line text-danger"
                                            }
                                        />

                                    </button>
                                    <small className="text-muted fs-6">
                                        {likesCount} Likes
                                    </small>
                                </div> */}
                            </div>
                        </div>

                        {/* =============================================
                            ARTICLE CONTENT
                        ============================================= */}

                        <article
                            ref={articleContentRef}
                            className="knowledge-v3-content"
                            onMouseUp={handleTextSelection}
                            onTouchEnd={handleTextSelection}
                            dangerouslySetInnerHTML={{
                                __html: selectedArticle?.details || "",
                            }}
                        />

                        {/* =============================================
                            ARTICLE FOOTER
                        ============================================= */}

                        <section className="knowledge-v3-footer">
                            <div className="knowledge-v3-footer-card">
                                <div className="knowledge-v3-footer-icon">
                                    <i className="ri-lightbulb-flash-line" />
                                </div>

                                <div>
                                    <strong>Enjoyed this insight?</strong>

                                    <p>
                                        Save useful ideas, share them with your network, and keep
                                        exploring more Salesforce knowledge from Longswitch Academy.
                                    </p>
                                </div>
                            </div>

                            {/* =========================================
                                NAVIGATION
                            ========================================= */}

                            <div className="knowledge-v3-navigation">
                                <button
                                    type="button"
                                    className={`knowledge-v3-nav-btn ${prevData ? "" : "disabled"
                                        }`}
                                    disabled={!prevData}
                                    onClick={() => onReadMore(prevData)}
                                >
                                    <i className="ri-arrow-left-line" />

                                    <span>
                                        <small>Previous</small>
                                        Previous Insight
                                    </span>
                                </button>

                                <div className="knowledge-v3-complete">
                                    <div className="knowledge-v3-complete-progress">
                                        <div
                                            style={{
                                                width: `${completionPercentage}%`,
                                            }}
                                        />
                                    </div>

                                    <span>
                                        {isReadingComplete
                                            ? "Reading complete"
                                            : `${completionPercentage}% read`}
                                    </span>
                                </div>

                                <button
                                    type="button"
                                    className={`knowledge-v3-nav-btn next ${nextData ? "" : "disabled"
                                        }`}
                                    disabled={!nextData}
                                    onClick={() => onReadMore(nextData)}
                                >
                                    <span>
                                        <small>Up next</small>
                                        Next Insight
                                    </span>

                                    <i className="ri-arrow-right-line" />
                                </button>
                            </div>
                        </section>
                    </main>

                    {/* =================================================
                        SELECTION TOOLBAR
                    ================================================= */}

                    {annotationToolbar.visible && selection?.text && (
                        <div
                            ref={selectionToolbarRef}
                            className="knowledge-annotation-toolbar"
                            style={{
                                left: annotationToolbar.x,
                                top: annotationToolbar.y,
                            }}
                            onMouseDown={(event) => event.preventDefault()}
                        >
                            {/* <button
                                    type="button"
                                    onClick={handleHighlight}
                                    title="Highlight"
                                >

                                    <i className="ri-mark-pen-line" />

                                    <span>
                                        Highlight
                                    </span>

                                </button>


                                <div className="knowledge-toolbar-divider" /> */}

                            <button type="button" onClick={saveQuote} title="Save quote">
                                <i className="ri-double-quotes-l" />

                                <span>Save</span>
                            </button>

                            <button
                                type="button"
                                onClick={searchGoogle}
                                title="Search Google"
                            >
                                <i className="ri-google-line" />
                            </button>

                            <button
                                type="button"
                                onClick={() => copyQuote(selection.text)}
                                title="Copy"
                            >
                                <i className="ri-file-copy-line" />
                            </button>

                            <button type="button" onClick={shareQuote} title="Share">
                                <i className="ri-share-forward-line" />
                            </button>
                        </div>
                    )}

                    {/* =================================================
                        TOAST
                    ================================================= */}

                    {toast && (
                        <div className="knowledge-v3-toast">
                            <i className="ri-checkbox-circle-fill" />

                            {toast}
                        </div>
                    )}
                </div>
            )}

            {showQuotes && (
                <div
                    className="knowledge-quotes-overlay"
                    onMouseDown={(event) => {
                        if (event.target === event.currentTarget) {
                            setShowQuotes(false);
                        }
                    }}
                >
                    <div className="knowledge-quotes-modal">
                        <div className="knowledge-quotes-header">
                            <div>
                                <div className="knowledge-quotes-eyebrow">YOUR NOTES</div>

                                <h3>Saved Quotes</h3>

                                <p>Your saved insights stay on this device.</p>
                            </div>

                            <button
                                type="button"
                                className="knowledge-quotes-close"
                                onClick={() => setShowQuotes(false)}
                            >
                                <i className="ri-close-line" />
                            </button>
                        </div>

                        <div className="knowledge-quotes-body">
                            {savedQuotes.length === 0 ? (
                                <div className="knowledge-quotes-empty">
                                    <div className="knowledge-quotes-empty-icon">
                                        <i className="ri-double-quotes-l" />
                                    </div>

                                    <h4>No saved quotes yet</h4>

                                    <p>
                                        Select an interesting sentence while reading and save it
                                        here.
                                    </p>
                                </div>
                            ) : (
                                savedQuotes.map((quote) => (
                                    <div key={quote.id} className="knowledge-quote-card">
                                        <div className="knowledge-quote-mark">“</div>

                                        <blockquote>{quote.text}</blockquote>

                                        <div className="knowledge-quote-source">
                                            <i className="ri-book-open-line" />

                                            {quote.articleTitle}
                                        </div>

                                        <div className="knowledge-quote-actions">
                                            <button
                                                type="button"
                                                onClick={() => copyQuote(quote.text)}
                                            >
                                                <i className="ri-file-copy-line" />
                                                Copy
                                            </button>

                                            <button
                                                type="button"
                                                onClick={() => shareSavedQuote(quote)}
                                            >
                                                <i className="ri-share-line" />
                                                Share
                                            </button>

                                            <button
                                                type="button"
                                                className="danger"
                                                onClick={() => deleteQuote(quote.id)}
                                            >
                                                <i className="ri-delete-bin-line" />
                                                Remove
                                            </button>
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                    </div>
                </div>
            )}

            <ModalLogin
                show={isLoggedOut}
                onCloseModalLogin={closeModalLogin}
                title={`Login to ${text} the Salesforce Quick Insights`}
            />
        </div>
    );
}

export default KnowledgeArticle;
